/**
 * `workingHours` is an untyped `object` on the BFF contract (design Q5 — still open). This module
 * models the hub's actual shape (`WorkingHoursEvaluator`, `domain-conversation` skill) so
 * `WorkingHoursField` can render and edit it safely without trusting the wire shape blindly:
 *
 * ```
 * { "timezone": "Europe/Moscow", "days": [ { "dayOfWeek": 1..7, "open": "HH:mm", "close": "HH:mm", "enabled": true }, … ] }
 * ```
 *
 * Nothing set, or a value that doesn't parse = no restriction (hours are open around the clock);
 * `close < open` wraps past midnight (evaluated hub-side, not re-validated here); `open === close`
 * closes the whole day. 1 = Monday … 7 = Sunday, matching `WEEKDAYS`/`STORE_DAY_OF_WEEK_OPTIONS` in
 * `store-settings/ui/options.ts`.
 */

export interface ChatbotWorkingHoursDay {
  dayOfWeek: number;
  open: string;
  close: string;
  enabled: boolean;
}

export interface ChatbotWorkingHours {
  timezone: string;
  days: ChatbotWorkingHoursDay[];
}

const DEFAULT_OPEN = '09:00';
const DEFAULT_CLOSE = '21:00';

function defaultDays(): ChatbotWorkingHoursDay[] {
  return Array.from({ length: 7 }, (_, index) => ({
    dayOfWeek: index + 1,
    open: DEFAULT_OPEN,
    close: DEFAULT_CLOSE,
    enabled: false,
  }));
}

function isTimeString(value: unknown): value is string {
  return typeof value === 'string' && /^\d{2}:\d{2}$/.test(value);
}

/**
 * `{}` (or `null`/no `days` array at all) is the platform default and means **no restriction** — a
 * person counts as available at any time (design §17.1.6, closing the shipped defect where this
 * rendered as "closed all week": the hub itself treats an unreadable/absent schedule as unrestricted).
 * An explicit save of all seven days disabled is a *different*, deliberate state (§17.1.6's "Every
 * day is off" warning) and still reports `true` here — the distinction is "was `days` ever written",
 * not "is every day currently off".
 */
export function hasWorkingHoursRestriction(
  raw: Record<string, unknown> | null | undefined,
): boolean {
  return Array.isArray(raw?.days) && (raw!.days as unknown[]).length > 0;
}

/** Best-effort, defensive parse of the untyped wire value — never throws. */
export function parseWorkingHours(
  raw: Record<string, unknown> | null | undefined,
): ChatbotWorkingHours {
  const timezone = typeof raw?.timezone === 'string' ? raw.timezone : '';
  const rawDays = Array.isArray(raw?.days) ? (raw.days as unknown[]) : [];
  const byDay = new Map<number, ChatbotWorkingHoursDay>();
  for (const entry of rawDays) {
    if (!entry || typeof entry !== 'object') continue;
    const day = entry as Record<string, unknown>;
    const dayOfWeek = typeof day.dayOfWeek === 'number' ? day.dayOfWeek : NaN;
    if (!Number.isInteger(dayOfWeek) || dayOfWeek < 1 || dayOfWeek > 7) continue;
    byDay.set(dayOfWeek, {
      dayOfWeek,
      open: isTimeString(day.open) ? day.open : DEFAULT_OPEN,
      close: isTimeString(day.close) ? day.close : DEFAULT_CLOSE,
      enabled: day.enabled === true,
    });
  }
  const days = defaultDays().map((fallback) => byDay.get(fallback.dayOfWeek) ?? fallback);
  return { timezone, days };
}

/** Serialises back to the wire shape — always the full 7-day array (the field is not sparse). */
export function serializeWorkingHours(value: ChatbotWorkingHours): Record<string, unknown> {
  return {
    timezone: value.timezone,
    days: value.days.map((day) => ({ ...day })),
  };
}

/**
 * "Set working hours" seed (design §17.1.6): Mon–Fri 09:00–21:00 on, Sat–Sun off, the tenant's
 * timezone when the page can read it (`settings.tenant.view`), else empty (the empty-timezone
 * client-side error then blocks Save until one is entered — an empty timezone would otherwise
 * silently read back as "no restriction").
 */
export function seedWorkingHours(timezone: string): ChatbotWorkingHours {
  return {
    timezone,
    days: defaultDays().map((day) => ({
      ...day,
      enabled: day.dayOfWeek >= 1 && day.dayOfWeek <= 5,
    })),
  };
}

/** True once a working-hours value with a restriction has every day switched off (design §17.1.6's
 * non-blocking "Every day is off" warning). */
export function isEveryDayOff(value: ChatbotWorkingHours): boolean {
  return value.days.every((day) => !day.enabled);
}
